import React, { useState } from "react";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import { UI_BODY_SEMIBOLD_SM_DF, UI_BODY_SM_DF, colors2, ButtonGhostV2 } from "oolib";
import { ChartBarHorizontalIcon } from "@phosphor-icons/react";
import { useGetTypeCategoryAndTags } from "../../../components/inputs/DynamicTagsInputs/utils";
import { PrevalenceSections as PrevalenceSectionsRaw, buildSubThemeColorIdxMap } from "@/components/analysisModules/comps/AnnotationPrevalence";
import { useGetTopicClusteringTree } from "@/utils/react-query-hooks/aiAnnotation";
import { useGetQueryData } from "@/utils/react-query-hooks/general";
import { getAnnoMode } from "@/utils/getAnnoMode";
import { groupDocTopicsFromTree } from "./groupDocTopicsFromTree";
import { t } from "../theme";

const PrevalenceSections = PrevalenceSectionsRaw as React.FC<any>;

// Outer padding lives on the sidebar <Card> (16px); this shell adds none.
const Box = styled.div`
  display: flex;
  flex-direction: column;
  // gap: 0.8rem;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
`;

const Reveal = styled(motion.div)`
  overflow: hidden;
`;

// The sections scroll within the height the host gives them (`scrollMaxHeight`
// — the AI panel hands over its full visible height; the sidebar card, which
// shares its column with the annotation list, this cap), so a document with
// many groups never pushes the page. The bleed margin lets the charts span
// past the sidebar Card's 16px padding, as the Highlights panel's do.
const SIDEBAR_SECTIONS_HEIGHT = 420;

const SectionsScroll = styled.div`
  overflow-y: auto;
  margin: 0 -16px -16px;
  padding-top: 0.4rem;

  /* recharts renders its <svg> with tabindex="0"; clicking a bar focuses it
     and Chrome paints a focus ring around the whole chart. The descendant
     combinator (note the leading space) targets the inner svg, not this div. */
  &:focus,
  &:focus-visible,
  & *:focus,
  & *:focus-visible {
    outline: none;
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.6rem;
  padding: 2.4rem 1rem;
  text-align: center;
`;

export type SubThemeGroup = {
  subThemeId: string;
  subThemeDisplay: string;
  total: number;
  // This group's share of the document's annotations (sub-theme groups) or
  // observations (topic groups) — what the section header prints.
  percentage?: number;
  tags: any[];
};

export type SelectedTag = {
  subThemeId: string;
  subThemeLabel: string;
  _id: string;
  tagLabel: string;
  // Present on a topic bar: the annoKeys of this document's observations
  // under that sub-topic.
  annoKeys?: string[];
};

/**
 * This document's prevalence, in the Highlights panel's own sections: one
 * titled section per group, each a bar chart — the shared PrevalenceSections,
 * so the document page, the Highlights panel and the Annotation Counts widget
 * read the same way. The data is the document's: its groups and colour map
 * come straight off `contribution` (so it drops anywhere with just that —
 * sidebar card, AI panel), and in split mode its topics from the clustering
 * tree, each charting its sub-topics. The tag/subTheme queries are
 * react-query-cached, so multiple mounts don't refetch.
 */
export const DistributionCardContent: React.FC<{
  contribution: any;
  activeTagId?: string;
  onSelectTag?: (tag: SelectedTag) => void;
  /** The height the sections may take before scrolling — the host's visible room. */
  scrollMaxHeight?: number | string;
}> = ({ contribution, activeTagId, onSelectTag, scrollMaxHeight = SIDEBAR_SECTIONS_HEIGHT }) => {
  const tagsQuery: any = useGetTypeCategoryAndTags({ tagTypeProp: "tags", enableDataFetch: true } as any);
  const subThemesQuery: any = useGetTypeCategoryAndTags({ tagTypeProp: "subThemes", enableDataFetch: true } as any);
  const tagDocs: any[] = Array.isArray(tagsQuery?.data) ? tagsQuery.data : [];
  const subThemeDocs: any[] = Array.isArray(subThemesQuery?.data) ? subThemesQuery.data : [];

  // Split mode: after the sub-themes (just Signals there), one dropdown entry
  // per topic this document has observations in, its sub-topics as the bars.
  // Read off the tenant's clustering tree — the same cached query the Topics
  // tab uses — which lists every observation with its documentId.
  const platformConfigs: any = useGetQueryData("platformConfigs");
  const isSplitMode = getAnnoMode(platformConfigs) === "split";
  const { data: treeData } = useGetTopicClusteringTree({ enabled: isSplitMode });
  const topicGroups = React.useMemo(
    () => (isSplitMode ? groupDocTopicsFromTree({ treeData, documentId: contribution?._id }) : []),
    [isSplitMode, treeData, contribution?._id],
  );

  const tagDocMap = React.useMemo(() => {
    const m: Record<string, any> = {};
    tagDocs.forEach((tg) => {
      m[tg._id] = tg;
    });
    return m;
  }, [tagDocs]);

  const annotationData = React.useMemo(() => {
    const data: any[] = contribution?.annotations?.tags?.tags?.data || [];
    return data.map((tg) => ({
      value: tg._id,
      display: tg.display || tagDocMap[tg._id]?.display || tg._id,
      count: Array.isArray(tg.fragments) ? tg.fragments.length : tg.count || 0,
    }));
  }, [contribution, tagDocMap]);

  const subThemeGroups: SubThemeGroup[] = React.useMemo(
    () => groupBySubTheme({ annotationData, tagDocs, subThemeDocs }),
    [annotationData, tagDocs, subThemeDocs],
  );
  // Each group's share of its own universe: a sub-theme group's of the
  // document's annotations, a topic group's of its observations — the
  // section header's "38% (12)".
  const withShare = (list: SubThemeGroup[]) => {
    const all = list.reduce((s, g) => s + (g.total || 0), 0);
    return list.map((g) => ({ ...g, percentage: all ? (100 * (g.total || 0)) / all : undefined }));
  };
  const groups: SubThemeGroup[] = React.useMemo(
    () => (topicGroups.length ? [...withShare(subThemeGroups), ...withShare(topicGroups as any)] : withShare(subThemeGroups)),
    [subThemeGroups, topicGroups],
  );
  // Sub-theme colour slots first, then one per topic — the slot order the
  // Annotation Counts widget uses, so a topic wears the same colour here.
  const colorIdxMap = React.useMemo(() => {
    const map: Record<string, number> = { ...buildSubThemeColorIdxMap(subThemeDocs) };
    const offset = Object.keys(map).length;
    topicGroups
      .map((g) => g.subThemeId)
      .sort()
      .forEach((id, i) => {
        map[id] = offset + i;
      });
    return map;
  }, [subThemeDocs, topicGroups]);

  // No grouped annotations. The sidebar host (DistributionCard) short-circuits
  // before reaching here, so this empty state only renders in the AI-panel tab,
  // where the Distribution tab is always present and needs something to show.
  if (!groups.length)
    return (
      <EmptyState onClick={(e: React.MouseEvent) => e.stopPropagation()}>
        <ChartBarHorizontalIcon size={24} weight="bold" color={colors2.grey40} />
        <UI_BODY_SM_DF style={{ color: colors2.grey60 }}>
          No distribution yet. Once this document has grouped annotations, their
          prevalence will show up here.
        </UI_BODY_SM_DF>
      </EmptyState>
    );

  return (
    <SectionsScroll style={{ maxHeight: scrollMaxHeight }} onClick={(e: React.MouseEvent) => e.stopPropagation()}>
      <PrevalenceSections
        groups={groups}
        colorIdxMap={colorIdxMap}
        sectionPadding="0 16px"
        highlightValue={activeTagId}
        showCount
        onBarClick={(v: any, group: SubThemeGroup) => {
          const bar = group.tags.find((b: any) => String(b.value) === String(v.value));
          onSelectTag?.({
            subThemeId: group.subThemeId,
            subThemeLabel: group.subThemeDisplay,
            _id: v.value,
            tagLabel: v.display ?? v.name,
            // Topic bars: the page filters cards by these rather than by a
            // tag id (topic membership lives on the tree, not on the
            // annotation's tags).
            ...(bar?.annoKeys ? { annoKeys: bar.annoKeys } : {}),
          });
        }}
      />
    </SectionsScroll>
  );
};

/**
 * Single-document subtheme prevalence: pick a subTheme → bar chart of its tags
 * by how many times each is applied in THIS document.
 *
 * Reuses the AnnotationExplorer prevalence machinery (BarChart, the subTheme
 * colour map, the tag/subTheme doc hooks). The grouping is re-implemented here
 * (small) — the original `groupAnnotationDataBySubTheme` lives inside
 * AnnotationExplorerVizPanel, which we don't touch. Data comes from the doc's
 * own consolidated annotations (count = number of annotation fragments), so no
 * collection-wide endpoint is needed.
 */
const groupBySubTheme = ({ annotationData, tagDocs, subThemeDocs }: any) => {
  if (!annotationData?.length || !tagDocs?.length) return [];
  const subThemeDisplayMap: Record<string, string> = {};
  subThemeDocs.forEach((st: any) => {
    subThemeDisplayMap[st._id] = st.display;
  });
  const tagDocMap: Record<string, any> = {};
  tagDocs.forEach((tg: any) => {
    tagDocMap[tg._id] = tg;
  });

  const groups: Record<string, any> = {};
  const UNGROUPED = "__ungrouped__";
  annotationData.forEach((row: any) => {
    const tagDoc = tagDocMap[row.value];
    const firstSubTheme = tagDoc?.tags?.subThemes?.data?.[0];
    const key = firstSubTheme?._id || UNGROUPED;
    const display = firstSubTheme
      ? subThemeDisplayMap[firstSubTheme._id] || firstSubTheme.display || "Ungrouped"
      : "Ungrouped";
    if (!groups[key]) {
      groups[key] = { subThemeId: key, subThemeDisplay: display, total: 0, tags: [] };
    }
    groups[key].total += row.count || 0;
    groups[key].tags.push(row);
  });

  return Object.values(groups)
    .map((g: any) => ({
      ...g,
      tags: [...g.tags].sort((a, b) => (b.count || 0) - (a.count || 0)),
    }))
    .sort((a: any, b: any) => b.total - a.total);
};

export const DistributionCard: React.FC<{
  contribution: any;
  activeTagId?: string;
  onSelectTag?: (tag: SelectedTag) => void;
}> = ({ contribution, activeTagId, onSelectTag }) => {
  const tagsQuery: any = useGetTypeCategoryAndTags({ tagTypeProp: "tags", enableDataFetch: true } as any);
  const subThemesQuery: any = useGetTypeCategoryAndTags({ tagTypeProp: "subThemes", enableDataFetch: true } as any);
  const tagDocs: any[] = Array.isArray(tagsQuery?.data) ? tagsQuery.data : [];
  const subThemeDocs: any[] = Array.isArray(subThemesQuery?.data) ? subThemesQuery.data : [];
  const [expanded, setExpanded] = useState(true);

  const tagDocMap = React.useMemo(() => {
    const m: Record<string, any> = {};
    tagDocs.forEach((tg) => {
      m[tg._id] = tg;
    });
    return m;
  }, [tagDocs]);

  const annotationData = React.useMemo(() => {
    const data: any[] = contribution?.annotations?.tags?.tags?.data || [];
    return data.map((tg) => ({
      value: tg._id,
      display: tg.display || tagDocMap[tg._id]?.display || tg._id,
      count: Array.isArray(tg.fragments) ? tg.fragments.length : tg.count || 0,
    }));
  }, [contribution, tagDocMap]);

  const groups = React.useMemo(
    () => groupBySubTheme({ annotationData, tagDocs, subThemeDocs }),
    [annotationData, tagDocs, subThemeDocs],
  );

  if (!groups.length) return null; // no annotations in this doc → no widget

  return (
    <Box>
      <Header onClick={() => setExpanded((p) => !p)}>
        <TitleRow>
          <ChartBarHorizontalIcon size={16} weight="bold" color={t("grey80")} />
          <UI_BODY_SEMIBOLD_SM_DF style={{ color: t("grey80") }}>Distribution</UI_BODY_SEMIBOLD_SM_DF>
        </TitleRow>
        <ButtonGhostV2
          icon={expanded ? "CaretUp" : "CaretDown"}
          iconColor={t("grey80")}
          style={{ padding: 4 }}
        />
      </Header>
      <AnimatePresence initial={false}>
        {expanded && (
          <Reveal
            key="reveal"
            initial={{ height: 0 }}
            animate={{ height: "auto" }}
            exit={{ height: 0 }}
            transition={{ duration: 0.27, ease: [0.32, 0.72, 0, 1] }}
          >
            <DistributionCardContent
              contribution={contribution}
              activeTagId={activeTagId}
              onSelectTag={onSelectTag}
            />
          </Reveal>
        )}
      </AnimatePresence>
    </Box>
  );
};

export default DistributionCard;
